import { useEffect, useMemo, useState } from 'react'
import { dayInMs } from '../../utils/constants'
import GraphButton from '../ui/Button'
import CustomCalendar from '../ui/CustomCalendar'
import PopoverMenu from '../ui/menuPopover/MenuPopover'
import { useGraphContext } from '../../context/graphContext'
import { checkIfSameDate } from './graphUtils'
import styles from './Graph.module.css'

const updater = (prevBrush, brushRef, scaledRange) => {
  const newExtent = brushRef.current?.getExtent(
    { x: scaledRange[0] },
    { x: scaledRange[1] },
  )
  const newState = {
    ...prevBrush,
    start: { y: newExtent.y0, x: newExtent.x0 },
    end: { y: newExtent.y1, x: newExtent.x1 },
    extent: newExtent,
  }
  return newState
}


export default function GraphBrushButtons({brushRef ,setBrushRange, xScale, resetRange, brushRange, activeColor}) {
  const { updateTimeFrame, timeFrame } = useGraphContext()
  const [ customBtnOpen, setCustomBtnOpen ] = useState(null)
  const [ CustomCalendarRange, setCustomCalendarRange ] = useState({start: null,end: null})
  const endDate = useMemo(() => resetRange[1] || new Date(),[resetRange])

  useEffect(() => {
    (timeFrame?.start && timeFrame?.end) 
      && setCustomCalendarRange({...timeFrame})
      return () => {}
  },[timeFrame])

  const handleCloseCustomCalendar = () => setCustomBtnOpen(null)

  const handleOpenCustomCalendar = (e) => setCustomBtnOpen(e.currentTarget) 

  // moves brush x days back
  const handleMoveBrush = (start, end) => {
    if (brushRef?.current) {
      const scaledRange = [ xScale(start), xScale(end)]
      brushRef.current.updateBrush((prevBrush) => updater(prevBrush, brushRef, scaledRange))
      setBrushRange([start, end])
    }
  }

  const handleButtonClick = (e) => {
    const daysBack = e.currentTarget.name
    updateTimeFrame({daysBack})
    const start = endDate.clone().subtract(daysBack, 'days').valueOf()
    handleMoveBrush(start, endDate)
  }

  const handleCalendarSubmit = (date) => {
    const { start, end } = date
    updateTimeFrame({start, end})
    setCustomCalendarRange({start,end})
    handleMoveBrush(start, end)
    handleCloseCustomCalendar()
  }
  
  // cleares brush
  const handleClearClick = () => {
    if (brushRef?.current) {
      setBrushRange([null, new Date()])
      brushRef.current.reset()
    }
  }

  const buttons =  useMemo(()=> [
    { daysBack: 30, label: '1M' },
    { daysBack: 365/2, label: '6M' },
    { daysBack: 365, label: '1Y' },
    { daysBack: 730, label: '2Y' },
    { daysBack: 1825, label: '5Y' },
  ],[])

  const active = useMemo(()=> buttons.find(
    ({daysBack})=> {
      const days = (brushRange[1] - brushRange[0] ) / dayInMs
      return days + 5 > daysBack && days - 5 < daysBack
      })?.label
  ,[brushRange, buttons])

  const customButton = (
    <>
      <GraphButton 
        outlinecolor={activeColor}
        $active={checkIfSameDate(CustomCalendarRange.start, brushRange[0]) && checkIfSameDate(CustomCalendarRange.end, brushRange[1])} 
        onClick={handleOpenCustomCalendar}>
          Custom
      </GraphButton>
      <PopoverMenu
        anchorEl={customBtnOpen}
        onClose={handleCloseCustomCalendar}>
          <CustomCalendar initialDate={CustomCalendarRange} handleCalendarSubmit={handleCalendarSubmit} handleClose={handleCloseCustomCalendar} />
      </PopoverMenu>
    </>
  )

  const allButton = (
    <GraphButton outlinecolor={activeColor} $active={!brushRange[0]} onClick={handleClearClick}>All</GraphButton>
  )

  return (
    <div className={`${styles['h-stack']} ${styles['button-group']}`}>
      {
        buttons.map(({daysBack, label}) => (
        <GraphButton
            outlinecolor={activeColor}
            $active={active === label}
            key={`brushfilterbutton-${daysBack}`}
            size="small"
            name={daysBack}
            onClick={handleButtonClick}
          >
            {label}
          </GraphButton>
        ))
      }
      {customButton}
      {allButton}
    </div>
  )
}